import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { VENDOR_CREDITS_ROUTES } from "@routes/vendorCredits"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    VENDOR_CREDITS_LIST_CONTROLS_ACTIVE_TAB,
    VENDOR_CREDITS_LIST_CONTROLS_KEYWORD_SEARCH,
    VENDOR_CREDITS_LIST_CONTROLS_PHASE,
    VENDOR_CREDITS_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import {
    VENDOR_CREDIT_CARDS_VIEW_ATTRIBUTE_OPTIONS,
    VENDOR_CREDIT_CARDS_VIEW_SORT_OPTIONS,
} from "./VendorCreditList.constants"
import VendorCreditObjectsViewTabs from "./VendorCreditObjectsViewTabs"
import VendorCreditCardsView from "./views/VendorCreditCardsView/VendorCreditCardsView"
import VendorCreditTableView from "./views/VendorCreditTableView/VendorCreditTableView"

export default function VendorCreditList() {
    const isDesktop = useIsDesktop()
    const { hasPermission } = useHasAccess()
    const [createRoute] = useSlugExtractor([VENDOR_CREDITS_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="vendor-credit-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={VENDOR_CREDITS_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle label={objectMap.vendor_credit.labelPlural} />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            bills_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                bills_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Vendor credits" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            bills_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={VENDOR_CREDITS_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={VENDOR_CREDITS_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={VENDOR_CREDITS_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Vendor Credits"
                            objectType="vendor_credit"
                            objectIndexName="VendorCredit"
                            objectName="Vendor Credit"
                            defaultActivePhase="open"
                            objectCreateRoute={createRoute}
                            isRowClickable={hasPermission("bills_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "bills_create_permission",
                                PERMISSION_LEVEL.RESTRICTED,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={VENDOR_CREDIT_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={VENDOR_CREDIT_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="open" title="Open" />
                                                <ObjectsView.Phase phase="closed" title="Closed" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <VendorCreditObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <VendorCreditTableView />}
                                    {activeViewType === "cards" && <VendorCreditCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
